import React, { useState, useEffect, useCallback } from "react";
import { useWindowWidth } from "../../hooks/useWindowWidth";
import { getContractsByEstimate } from "../../services/azureCostEstimateContractsService.js";
import { getPaymentsByEstimate } from "../../services/azureCostEstimatePaymentsService.js";

const BREAKPOINT = 1280;
const STORAGE_KEY = "estimateSidebarOpen";

const getAccuracyFromPhase = (phase) => {
  if (!phase) return 25;
  const phaseNum = parseInt(phase.substring(0, 2));
  if (phaseNum === 21) return 25;
  if (phaseNum === 31) return 15;
  return 10;
};

const EstimateSidebar = ({
  onBack,
  estimateName,
  linkedProject,
  currentPhase,
  phases,
  onSwitchPhase,
  onAddPhase,
  onRenamePhase,
  onDeletePhase,
  estimateId,
  levelFilter,
  setLevelFilter,
  showInactive,
  setShowInactive,
  readOnly = false,
  variants = [],
  onCreateVariant,
  onToggleVariant,
  onDeleteVariant,
  onApplyVariant,
  getVariantConflicts,
  searchTerm = "",
  setSearchTerm,
  activeFilters = [],
  addFilter,
  removeFilter,
  filterSums = false,
  setFilterSums,
  // Objektgliederung der aktuellen Kostenkontroll-Phase (US-KOST-023, ADR 0064
  // Nachtrag 2026-09-30): gilt für alle Zahlungsanweisungen dieser Phase.
  objektgliederung = "",
  onSaveObjektgliederung, // async (value) => void — null = kein Schreibrecht
}) => {
  const windowWidth = useWindowWidth();
  const isControlPhase = (currentPhase || "").trim().startsWith("41");

  // Lokaler Eingabewert; gespeichert wird bei Blur/Enter, wenn geaendert.
  const [ogValue, setOgValue] = useState(objektgliederung || "");
  const [ogStatus, setOgStatus] = useState(""); // "" | "saving" | "saved" | "error"
  useEffect(() => {
    setOgValue(objektgliederung || "");
  }, [objektgliederung]);
  useEffect(() => {
    if (ogStatus !== "saved") return undefined;
    const t = setTimeout(() => setOgStatus(""), 2500);
    return () => clearTimeout(t);
  }, [ogStatus]);

  const commitObjektgliederung = async () => {
    if (!onSaveObjektgliederung) return;
    const next = ogValue.trim();
    if (next === (objektgliederung || "").trim()) return;
    setOgStatus("saving");
    try {
      await onSaveObjektgliederung(next);
      setOgStatus("saved");
    } catch (err) {
      console.error("Objektgliederung speichern fehlgeschlagen:", err);
      setOgStatus("error");
    }
  };
  const [newVariantName, setNewVariantName] = useState("");

  const [variantContextMenu, setVariantContextMenu] = useState(null);

  // Neue Phase (US-KOST-019): Modal fragt, ob Daten aus einer bestehenden
  // Phase übernommen werden sollen. { name, mode: 'empty'|'copy', sourcePhase, busy, error }
  const [phaseModal, setPhaseModal] = useState(null);
  // Phase umbenennen (US-KOST-019): { oldName, newName, busy, error }
  const [renameModal, setRenameModal] = useState(null);
  // Phase löschen (US-KOST-019): { phase, busy, error, counts: null|{contracts, payments} }
  const [deletePhaseModal, setDeletePhaseModal] = useState(null);

  const [isOpen, setIsOpen] = useState(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored !== null) return stored === "true";
    return windowWidth >= BREAKPOINT;
  });

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, String(isOpen));
  }, [isOpen]);

  const toggleSidebar = () => setIsOpen((prev) => !prev);

  // Close variant context menu on click outside
  useEffect(() => {
    if (!variantContextMenu) return;
    const close = () => setVariantContextMenu(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [variantContextMenu]);

  const openPhaseModal = () => {
    // newType nur relevant bei Kostenkontroll-Quelle: 'control' (vollständige
    // Arbeitskopie) oder 'estimate' (nur Positionsstruktur).
    setPhaseModal({ name: "", mode: "empty", sourcePhase: currentPhase, newType: "control", busy: false, error: "" });
  };

  // Effektiver Name der neuen Phase: Wird eine Kostenkontrolle als Kostenkontrolle
  // kopiert, braucht der Name das "41"-Präfix (Editor-Weiche, ADR 0014) — es wird
  // automatisch ergänzt und im Dialog angekündigt.
  const effectivePhaseName = (m) => {
    if (!m) return "";
    const raw = m.name.trim();
    if (!raw) return "";
    const copyFrom41 = m.mode === "copy" && m.sourcePhase?.trim().startsWith("41");
    const asControl = copyFrom41 && (m.newType || "control") === "control";
    if (asControl && !raw.startsWith("41")) return `41 - ${raw}`;
    return raw;
  };

  const confirmAddPhase = async () => {
    const m = phaseModal;
    const finalName = effectivePhaseName(m);
    if (!m || !finalName || m.busy) return;
    setPhaseModal((prev) => ({ ...prev, busy: true, error: "" }));
    try {
      await onAddPhase(finalName, {
        copyFromPhase: m.mode === "copy" ? m.sourcePhase : undefined,
      });
      setPhaseModal(null);
      // Direkt in die neue Phase wechseln — das ist nach dem Erstellen (mit
      // oder ohne Datenübernahme) das erwartete Ziel.
      onSwitchPhase(finalName);
    } catch (err) {
      setPhaseModal((prev) => ({
        ...prev,
        busy: false,
        error: err?.message || "Phase konnte nicht erstellt werden.",
      }));
    }
  };

  // Lösch-Dialog öffnen und Datenübersicht nachladen (Verträge/Zahlungen der
  // Phase; Varianten kommen aus dem Prop, weil dort nur die der aktuellen Phase
  // geladen sind — und gelöscht wird immer die aktuell gewählte Phase).
  const openDeletePhaseModal = () => {
    const phase = currentPhase;
    setDeletePhaseModal({ phase, busy: false, error: "", counts: null });
    if (!estimateId) return;
    Promise.all([
      getContractsByEstimate(estimateId, phase),
      getPaymentsByEstimate(estimateId, phase),
    ]).then(([contracts, payments]) => {
      setDeletePhaseModal((prev) =>
        prev && prev.phase === phase
          ? { ...prev, counts: { contracts: contracts.length, payments: payments.length } }
          : prev
      );
    });
  };

  const confirmDeletePhase = async () => {
    const m = deletePhaseModal;
    if (!m || m.busy) return;
    setDeletePhaseModal((prev) => ({ ...prev, busy: true, error: "" }));
    try {
      await onDeletePhase(m.phase);
      setDeletePhaseModal(null);
    } catch (err) {
      setDeletePhaseModal((prev) => ({
        ...prev,
        busy: false,
        error: err?.message || "Phase konnte nicht gelöscht werden.",
      }));
    }
  };

  const confirmRenamePhase = async () => {
    const m = renameModal;
    if (!m || !m.newName.trim() || m.busy) return;
    if (m.newName.trim() === m.oldName) {
      setRenameModal(null);
      return;
    }
    setRenameModal((prev) => ({ ...prev, busy: true, error: "" }));
    try {
      await onRenamePhase(m.oldName, m.newName.trim());
      setRenameModal(null);
    } catch (err) {
      setRenameModal((prev) => ({
        ...prev,
        busy: false,
        error: err?.message || "Phase konnte nicht umbenannt werden.",
      }));
    }
  };

  const handleCreateVariant = () => {
    if (!newVariantName.trim() || !onCreateVariant) return;
    onCreateVariant(newVariantName.trim());
    setNewVariantName("");
  };

  const accuracy = getAccuracyFromPhase(currentPhase);
  const conflicts = getVariantConflicts ? getVariantConflicts() : {};
  const hasConflicts = Object.keys(conflicts).length > 0;

  return (
      <div className={`estimate-sidebar-wrapper ${isOpen ? "open" : "closed"}`}>
        <aside className={`estimate-sidebar ${isOpen ? "open" : "closed"}`}>
          <div className="sidebar-content">
          <div className="sidebar-section">
            {onBack && (
              <button className="sidebar-back-btn" onClick={onBack} title="Zurück zur Übersicht">
                &larr; Übersicht
              </button>
            )}
            {linkedProject && (
              <div className="sidebar-project-info">
                {linkedProject.code} - {linkedProject.name}
              </div>
            )}
            <h3 className="sidebar-title">{estimateName || "Dossier"}</h3>
          </div>

          {setSearchTerm && (
            <div className="sidebar-section">
              <h4 className="sidebar-section-title">Suche</h4>
              <div className="search-row">
                <input
                  type="text"
                  className="sidebar-search-input"
                  placeholder="Suchen... (;=oder, &=und)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && searchTerm.trim() && addFilter) {
                      const match = searchTerm.match(/^(\w+):\s*(.+)$/);
                      if (match) {
                        addFilter(match[1], match[2].trim());
                      } else {
                        addFilter("name", searchTerm.trim());
                      }
                    }
                  }}
                />
                {setFilterSums && (
                  <button
                    className={`filter-sums-btn ${filterSums ? "active" : ""}`}
                    onClick={() => setFilterSums(!filterSums)}
                    title={filterSums ? "Gefilterte Summen aus" : "Nur gefilterte Positionen summieren"}
                  >
                    &Sigma;
                  </button>
                )}
              </div>
              {activeFilters.length > 0 && (
                <div className="active-filters">
                  {activeFilters.map((f, i) => (
                    <span key={i} className="filter-tag">
                      {f.column}: {f.value}
                      <button className="filter-remove" onClick={() => removeFilter(i)}>&times;</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="sidebar-section">
            <h4 className="sidebar-section-title">Phase</h4>
            <select
              className="sidebar-select"
              value={currentPhase}
              onChange={(e) => onSwitchPhase(e.target.value)}
            >
              {phases.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <div className="phase-accuracy">
              Genauigkeit: &plusmn;{accuracy}%
            </div>
            {!readOnly && (
              <div className="phase-actions">
                <button
                  className="phase-action-btn"
                  onClick={openPhaseModal}
                  title="Neue Phase erstellen — leer oder mit Daten aus einer bestehenden Phase"
                >
                  Neue Phase...
                </button>
                {onRenamePhase && (
                  <button
                    className="phase-action-btn"
                    onClick={() =>
                      setRenameModal({ oldName: currentPhase, newName: currentPhase, busy: false, error: "" })
                    }
                    title="Aktuell gewählte Phase umbenennen"
                  >
                    Umbenennen
                  </button>
                )}
                {onDeletePhase && (
                  <button
                    className="phase-action-btn danger"
                    onClick={openDeletePhaseModal}
                    disabled={phases.length <= 1}
                    title={
                      phases.length <= 1
                        ? "Die letzte Phase eines Dossiers kann nicht gelöscht werden"
                        : "Aktuell gewählte Phase mit allen Daten löschen"
                    }
                  >
                    Löschen
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Variants Section */}
          {isControlPhase && (
            <div className="sidebar-section">
              <h4 className="sidebar-section-title">Zahlungsanweisung</h4>
              <label className="sidebar-field-label" htmlFor="sidebar-objektgliederung">
                Objektgliederung
              </label>
              <input
                id="sidebar-objektgliederung"
                type="text"
                className="sidebar-search-input"
                value={ogValue}
                onChange={(e) => setOgValue(e.target.value)}
                onBlur={commitObjektgliederung}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.currentTarget.blur();
                }}
                placeholder="z.B. Gleispool Gesamtprojekt (GP)"
                title="Erscheint auf allen Zahlungsanweisungen dieser Phase"
                disabled={readOnly || !onSaveObjektgliederung}
              />
              <div className={`sidebar-hint${ogStatus === "error" ? " sidebar-hint--error" : ""}`}>
                {ogStatus === "saving" && "Wird gespeichert…"}
                {ogStatus === "saved" && "Gespeichert."}
                {ogStatus === "error" && "Speichern fehlgeschlagen. Bitte erneut versuchen."}
                {!ogStatus && "Gilt für alle Zahlungsanweisungen dieser Phase."}
              </div>
            </div>
          )}

          <div className="sidebar-section">
            <h4 className="sidebar-section-title">Varianten</h4>
            {hasConflicts && (
              <div className="variant-conflict-warning">
                Konflikt: {Object.keys(conflicts).length} BKP-Position(en) in mehreren aktiven Varianten
              </div>
            )}
            <div className="variant-list">
              {variants.length === 0 ? (
                <div className="variant-empty">Keine Varianten</div>
              ) : (
                variants.map((v) => (
                  <div
                    key={v.id}
                    className={`variant-item ${v.isActive ? "active" : ""}`}
                    onContextMenu={(e) => {
                      if (readOnly || !onApplyVariant || v.positions.length === 0) return;
                      e.preventDefault();
                      setVariantContextMenu({ x: e.clientX, y: e.clientY, variant: v });
                    }}
                  >
                    <label className="variant-toggle">
                      <input
                        type="checkbox"
                        checked={v.isActive}
                        onChange={() => onToggleVariant && onToggleVariant(v.id)}
                        disabled={readOnly}
                      />
                      <span className="variant-name">{v.name}</span>
                      <span className="variant-count">{v.positions.length}</span>
                    </label>
                    {!readOnly && onDeleteVariant && (
                      <button
                        className="variant-delete-btn"
                        onClick={() => {
                          if (window.confirm(`Variante "${v.name}" löschen?`)) {
                            onDeleteVariant(v.id);
                          }
                        }}
                        title="Variante löschen"
                      >
                        &times;
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
            {!readOnly && (
              <div className="add-phase-row">
                <input
                  type="text"
                  className="add-phase-input"
                  placeholder="Neue Variante..."
                  value={newVariantName}
                  onChange={(e) => setNewVariantName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleCreateVariant();
                  }}
                />
                <button
                  className="add-phase-btn"
                  onClick={handleCreateVariant}
                  disabled={!newVariantName.trim()}
                  title="Variante erstellen"
                >
                  +
                </button>
              </div>
            )}
          </div>

          <div className="sidebar-section">
            <h4 className="sidebar-section-title">Levelfilter</h4>
            <div className="level-filter-buttons">
              {[1, 2, 3, 4, 5].map((level) => (
                <button
                  key={level}
                  className={`level-btn ${levelFilter === level ? "active" : ""}`}
                  onClick={() => setLevelFilter(level)}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>

          <div className="sidebar-section">
            <label className="sidebar-checkbox">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              Inaktive anzeigen
            </label>
          </div>
        </div>
        </aside>
        <button
          className="sidebar-toggle-btn"
          onClick={toggleSidebar}
          title={isOpen ? "Sidebar zuklappen" : "Sidebar aufklappen"}
        >
          {isOpen ? "\u2039" : "\u203A"}
        </button>

        {/* Variant context menu */}
        {variantContextMenu && (
          <div
            className="context-menu"
            style={{ top: variantContextMenu.y, left: variantContextMenu.x, position: "fixed" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="context-menu-header">{variantContextMenu.variant.name}</div>
            <button
              className="context-menu-item"
              onClick={() => {
                const v = variantContextMenu.variant;
                if (window.confirm(`Variante "${v.name}" übernehmen? Die Überschreibungen werden als Standard gesetzt und die Variante wird entfernt.`)) {
                  onApplyVariant(v.id);
                }
                setVariantContextMenu(null);
              }}
            >
              Übernehmen ({variantContextMenu.variant.positions.length} Positionen)
            </button>
          </div>
        )}

        {/* Neue Phase erstellen (US-KOST-019): leer oder mit Datenübernahme */}
        {phaseModal && (
          <div
            className="modal-overlay"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget && !phaseModal.busy) setPhaseModal(null);
            }}
          >
            <div className="modal-content estimate-modal">
              <div className="modal-header">
                <h3>Neue Phase erstellen</h3>
                <button className="modal-close" onClick={() => setPhaseModal(null)} disabled={phaseModal.busy}>
                  &times;
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Name der neuen Phase</label>
                  <input
                    type="text"
                    placeholder='z.B. "33 - Bewilligung"'
                    value={phaseModal.name}
                    autoFocus
                    disabled={phaseModal.busy}
                    onChange={(e) => setPhaseModal((prev) => ({ ...prev, name: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") confirmAddPhase();
                    }}
                  />
                </div>
                <div className="form-group phase-mode-choice">
                  <label>
                    <input
                      type="radio"
                      name="new-phase-mode"
                      checked={phaseModal.mode === "empty"}
                      disabled={phaseModal.busy}
                      onChange={() => setPhaseModal((prev) => ({ ...prev, mode: "empty" }))}
                    />
                    Leer beginnen
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="new-phase-mode"
                      checked={phaseModal.mode === "copy"}
                      disabled={phaseModal.busy}
                      onChange={() => setPhaseModal((prev) => ({ ...prev, mode: "copy" }))}
                    />
                    Daten aus einer bestehenden Phase kopieren
                  </label>
                  {phaseModal.mode === "copy" && (
                    <select
                      value={phaseModal.sourcePhase}
                      disabled={phaseModal.busy}
                      onChange={(e) => setPhaseModal((prev) => ({ ...prev, sourcePhase: e.target.value }))}
                    >
                      {phases.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  )}
                </div>
                {/* Ziel "41 ..." = Kostenkontrolle: Quelle wird als KV-Basis
                    importiert statt roh kopiert (US-KOST-019 / KOST-006). */}
                {phaseModal.mode === "copy" &&
                  phaseModal.name.trim().startsWith("41") &&
                  !phaseModal.sourcePhase?.trim().startsWith("41") && (
                    <p className="override-hint">
                      Die neue Phase ist eine Kostenkontrolle („41"): Die Quell-Phase wird als
                      KV-Basis übernommen — pro bewerteter Position der aggregierte Total × 1.081
                      (MwSt) als fixer KV-Wert.
                    </p>
                  )}
                {/* Quelle ist eine Kostenkontrolle: Typ der neuen Phase wählen */}
                {phaseModal.mode === "copy" && phaseModal.sourcePhase?.trim().startsWith("41") && (
                  <div className="form-group phase-mode-choice">
                    <label>
                      <input
                        type="radio"
                        name="new-phase-type"
                        checked={(phaseModal.newType || "control") === "control"}
                        disabled={phaseModal.busy}
                        onChange={() => setPhaseModal((prev) => ({ ...prev, newType: "control" }))}
                      />
                      Als Kostenkontrolle kopieren (vollständig, inkl. Verträge und Zahlungen)
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="new-phase-type"
                        checked={phaseModal.newType === "estimate"}
                        disabled={phaseModal.busy}
                        onChange={() => setPhaseModal((prev) => ({ ...prev, newType: "estimate" }))}
                      />
                      Als Kostenschätzung anlegen (nur Positionsstruktur)
                    </label>
                  </div>
                )}
                {phaseModal.mode === "copy" &&
                  phaseModal.sourcePhase?.trim().startsWith("41") &&
                  (phaseModal.newType || "control") === "control" && (
                    <p className="override-hint">
                      Die Kostenkontrolle „{phaseModal.sourcePhase}" wird vollständig kopiert:
                      KV-Werte, Erwartungen sowie Verträge/Nachträge und Zahlungen (als unabhängige
                      Kopien — Änderungen wirken nicht auf das Original zurück).
                      {phaseModal.name.trim() && effectivePhaseName(phaseModal) !== phaseModal.name.trim() &&
                        ` Die Phase wird als „${effectivePhaseName(phaseModal)}" angelegt — das Präfix „41" kennzeichnet Kostenkontrollen.`}
                    </p>
                  )}
                {phaseModal.mode === "copy" &&
                  phaseModal.sourcePhase?.trim().startsWith("41") &&
                  phaseModal.newType === "estimate" && (
                    <>
                      <p className="override-hint">
                        Hinweis: Die neue Phase wird eine Kostenschätzung — KV-Werte, Verträge und
                        Zahlungen werden dort nicht angezeigt; die Positionen kommen ohne
                        Ausmasswerte an.
                      </p>
                      {phaseModal.name.trim().startsWith("41") && (
                        <div className="error-message">
                          Der Name beginnt mit „41" — solche Phasen öffnen immer die Kostenkontrolle.
                          Bitte einen Namen ohne „41"-Präfix wählen.
                        </div>
                      )}
                    </>
                  )}
                {phaseModal.error && <div className="error-message">{phaseModal.error}</div>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setPhaseModal(null)} disabled={phaseModal.busy}>
                  Abbrechen
                </button>
                <button
                  className="btn btn-primary"
                  onClick={confirmAddPhase}
                  disabled={phaseModal.busy || !phaseModal.name.trim()}
                >
                  {phaseModal.busy ? "Erstelle..." : "Erstellen"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Phase umbenennen (US-KOST-019) */}
        {renameModal && (
          <div
            className="modal-overlay"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget && !renameModal.busy) setRenameModal(null);
            }}
          >
            <div className="modal-content estimate-modal">
              <div className="modal-header">
                <h3>Phase umbenennen</h3>
                <button className="modal-close" onClick={() => setRenameModal(null)} disabled={renameModal.busy}>
                  &times;
                </button>
              </div>
              <div className="modal-body">
                <p className="override-hint">
                  Alle Daten der Phase „{renameModal.oldName}" (Positionen, Verträge, Zahlungen, Varianten)
                  werden auf den neuen Namen übertragen.
                </p>
                <div className="form-group">
                  <label>Neuer Name</label>
                  <input
                    type="text"
                    value={renameModal.newName}
                    autoFocus
                    disabled={renameModal.busy}
                    onChange={(e) => setRenameModal((prev) => ({ ...prev, newName: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") confirmRenamePhase();
                    }}
                  />
                </div>
                {renameModal.oldName.trim().startsWith("41") !==
                  renameModal.newName.trim().startsWith("41") && (
                  <div className="error-message">
                    Achtung: Der Name bestimmt den Editor-Typ — Phasen, die mit „41" beginnen, öffnen die
                    Kostenkontrolle, alle anderen die Kostenschätzung. Diese Umbenennung wechselt den Typ.
                  </div>
                )}
                {renameModal.error && <div className="error-message">{renameModal.error}</div>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setRenameModal(null)} disabled={renameModal.busy}>
                  Abbrechen
                </button>
                <button
                  className="btn btn-primary"
                  onClick={confirmRenamePhase}
                  disabled={
                    renameModal.busy ||
                    !renameModal.newName.trim() ||
                    renameModal.newName.trim() === renameModal.oldName
                  }
                >
                  {renameModal.busy ? "Benenne um..." : "Umbenennen"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Phase löschen (US-KOST-019) */}
        {deletePhaseModal && (
          <div
            className="modal-overlay"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget && !deletePhaseModal.busy) setDeletePhaseModal(null);
            }}
          >
            <div className="modal-content estimate-modal">
              <div className="modal-header">
                <h3>Phase löschen</h3>
                <button
                  className="modal-close"
                  onClick={() => setDeletePhaseModal(null)}
                  disabled={deletePhaseModal.busy}
                >
                  &times;
                </button>
              </div>
              <div className="modal-body">
                <p className="override-hint">
                  Phase „{deletePhaseModal.phase}" wird mit allen zugehörigen Daten gelöscht:
                  sämtliche BKP-Positionen und Werte
                  {deletePhaseModal.counts
                    ? `, ${deletePhaseModal.counts.contracts} Vertrag/Nachtrag${deletePhaseModal.counts.contracts === 1 ? "" : "e"}, ${deletePhaseModal.counts.payments} Zahlung${deletePhaseModal.counts.payments === 1 ? "" : "en"}`
                    : ", Verträge/Nachträge und Zahlungen (werden gezählt...)"}
                  {variants.length > 0
                    ? ` sowie ${variants.length} Variante${variants.length === 1 ? "" : "n"}.`
                    : "."}
                </p>
                <div className="error-message">
                  Achtung: Dieser Vorgang kann nicht rückgängig gemacht werden.
                </div>
                {deletePhaseModal.error && <div className="error-message">{deletePhaseModal.error}</div>}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setDeletePhaseModal(null)}
                  disabled={deletePhaseModal.busy}
                >
                  Abbrechen
                </button>
                <button
                  className="btn btn-primary"
                  onClick={confirmDeletePhase}
                  disabled={deletePhaseModal.busy}
                >
                  {deletePhaseModal.busy ? "Lösche..." : "Phase endgültig löschen"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

export default EstimateSidebar;
